import { useState, useCallback } from 'react';
import { Patient, Encounter, Practitioner } from '@medplum/fhirtypes';

export type OnboardingStep = 'search' | 'patient' | 'encounter' | 'confirmation';

export interface PatientOnboardingState {
  currentStep: OnboardingStep;
  patient: Patient | null;
  isNewPatient: boolean;
  encounter: Encounter | null;
  isExistingEncounter: boolean;
  attendingDoctor: Practitioner | null;
  referringDoctor: Practitioner | null;
}

export interface UsePatientOnboarding {
  state: PatientOnboardingState;
  goToStep: (step: OnboardingStep) => void;
  setPatient: (patient: Patient | null, isNew: boolean) => void;
  setEncounter: (encounter: Encounter, isExisting?: boolean) => void;
  setAttendingDoctor: (doctor: Practitioner | null) => void;
  setReferringDoctor: (doctor: Practitioner | null) => void;
  reset: () => void;
  canProceedToPatient: () => boolean;
  canProceedToEncounter: () => boolean;
  canProceedToConfirmation: () => boolean;
}

const initialState: PatientOnboardingState = {
  currentStep: 'search',
  patient: null,
  isNewPatient: false,
  encounter: null,
  isExistingEncounter: false,
  attendingDoctor: null,
  referringDoctor: null,
};

/**
 * Custom hook to manage patient onboarding workflow state
 * Handles patient search, creation, review, and encounter creation
 * @returns UsePatientOnboarding object with state and control methods
 */
export function usePatientOnboarding(): UsePatientOnboarding {
  const [state, setState] = useState<PatientOnboardingState>(initialState);

  const goToStep = useCallback((step: OnboardingStep) => {
    setState((prev) => ({ ...prev, currentStep: step }));
  }, []);

  const setPatient = useCallback((patient: Patient | null, isNew: boolean) => {
    setState((prev) => ({
      ...prev,
      patient,
      isNewPatient: isNew,
      currentStep: patient ? 'patient' : 'search',
    }));
  }, []);

  const setEncounter = useCallback((encounter: Encounter, isExisting = false) => {
    setState((prev) => ({
      ...prev,
      encounter,
      isExistingEncounter: isExisting,
      currentStep: 'confirmation',
    }));
  }, []);

  const setAttendingDoctor = useCallback((doctor: Practitioner | null) => {
    setState((prev) => ({ ...prev, attendingDoctor: doctor }));
  }, []);

  const setReferringDoctor = useCallback((doctor: Practitioner | null) => {
    setState((prev) => ({ ...prev, referringDoctor: doctor }));
  }, []);

  const reset = useCallback(() => {
    setState(initialState);
  }, []);

  const canProceedToPatient = useCallback(() => {
    return state.patient !== null;
  }, [state.patient]);

  const canProceedToEncounter = useCallback(() => {
    return state.patient?.id !== undefined;
  }, [state.patient]);

  const canProceedToConfirmation = useCallback(() => {
    return (
      state.patient !== null &&
      state.encounter !== null &&
      state.attendingDoctor !== null
    );
  }, [state.patient, state.encounter, state.attendingDoctor]);

  return {
    state,
    goToStep,
    setPatient,
    setEncounter,
    setAttendingDoctor,
    setReferringDoctor,
    reset,
    canProceedToPatient,
    canProceedToEncounter,
    canProceedToConfirmation,
  };
}
